import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 * HOTO India change (Oct 2026): the video only loads when it is actually shown
 * and close to the screen. On phones (under 750px) and with Data Saver on, the
 * video is not loaded at all.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoElement } = this.refs;
    const saveData = navigator.connection && navigator.connection.saveData;
    const isMobile = window.matchMedia('(max-width: 749px)').matches;

    if (saveData || isMobile) {
      videoElement.removeAttribute('autoplay');
      videoElement.setAttribute('preload', 'none');
      videoElement.style.display = 'none';
      return;
    }

    if (!('IntersectionObserver' in window)) {
      this.#loadVideo();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && this.getClientRects().length > 0) {
            this.#loadVideo();
            this.observer?.disconnect();
          }
        }
      },
      { rootMargin: '200px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.observer?.disconnect();
  }

  #loadVideo() {
    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}